import * as React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

export default function ExamDayComponent() {
  return (
    <Container
      id="exam-day"
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 8, sm: 16 },
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 3, sm: 6 }
      }}>
      <Typography
        component="h1"
        variant="h4"
        color="text.primary"
        sx={{
          width: { sm: '100%', md: '80%' },
          textAlign: 'center',
          mb: 4
        }}>
        📅 SCA Exam Day Tips
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: 2
        }}>
        <Box sx={{ width: '100%' }}>
          <Typography variant="h6" component="h2" gutterBottom>
            1. Treat It Like a Normal Clinic
          </Typography>
          <Typography variant="body1" paragraph>
            Approach each case as if it were a real consultation in your GP surgery. Keep your tone
            natural, empathetic, and efficient — you&apos;re not performing, you&apos;re being the
            GP you already are.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" component="h2" gutterBottom>
            2. Structure = Confidence
          </Typography>
          <Typography variant="body1" paragraph>
            Have a clear consultation framework in mind (e.g. ICE, data gathering, shared decision-
            making). It keeps you grounded and ensures you cover all key domains.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" component="h2" gutterBottom>
            3. It&apos;s a Marathon, Not a Sprint
          </Typography>
          <Typography variant="body1" paragraph>
            You&apos;ll do six 12-minute stations back-to-back before getting a break — it can be
            mentally and physically tiring. Make sure you&apos;ve:
          </Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 4 }}>
            <li>Had a proper meal before starting</li>
            <li>Brought water and snacks to keep your energy up during breaks</li>
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" component="h2" gutterBottom>
            4. Choose Your Exam Room Wisely
          </Typography>
          <Typography variant="body1" paragraph>
            You&apos;ll be sitting the exam in your own GP practice — often in a familiar room. Pick
            the room early and make sure:
          </Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 4 }}>
            <li>The audio/video setup works smoothly</li>
            <li>It&apos;s quiet, well-lit, and private</li>
            <li>You&apos;ve tested the IT equipment beforehand with no last-minute surprises</li>
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" component="h2" gutterBottom>
            5. Time Awareness, Not Time Pressure
          </Typography>
          <Typography variant="body1" paragraph>
            Keep one eye on the time but don&apos;t let it stress you out. Move things along
            naturally and avoid getting stuck on any single part of the consultation.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" component="h2" gutterBottom>
            6. Be Curious, Not Robotic
          </Typography>
          <Typography variant="body1" paragraph>
            Instead of checklist-style questions, show curiosity about the patient&apos;s story.
            This approach is more natural and often uncovers the key issue efficiently.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" component="h2" gutterBottom>
            7. Don&apos;t Dwell on One Bad Case
          </Typography>
          <Typography variant="body1" paragraph>
            If one case doesn&apos;t go well, let it go and reset. You&apos;re being assessed across
            multiple domains and stations — one tricky case won&apos;t ruin your whole exam.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" component="h2" gutterBottom>
            8. Red Flags & Safeguarding: Don&apos;t Miss Them
          </Typography>
          <Typography variant="body1" paragraph>
            If there&apos;s even a hint of red flag symptoms, safeguarding concerns, or risk issues
            — explore them. Examiners are watching for this.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" component="h2" gutterBottom>
            9. Close Every Case Properly
          </Typography>
          <Typography variant="body1" paragraph>
            End each consultation with a clear summary, safety-netting, and a follow-up plan. It
            shows confidence, leadership, and patient-centred care.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" component="h2" gutterBottom>
            10. Rest, Breathe, and Be Kind to Yourself
          </Typography>
          <Typography variant="body1" paragraph>
            Don&apos;t cram right before the exam. Get a good night&apos;s sleep, breathe between
            cases, and treat it as another day in clinic — just with a few extra cameras.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="body1" paragraph>
            <span role="img" aria-label="Link" style={{ marginRight: '10px' }}>
              🔗
            </span>
            For more official guidance, visit the{' '}
            <Link
              href="https://www.rcgp.org.uk/mrcgp-exams/simulated-consultation-assessment/Trainee-Surgery-Guide"
              target="_blank"
              rel="noopener noreferrer">
              RCGP SCA Trainee Surgery Guide
            </Link>{' '}
            on the RCGP website.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}
